import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiErr, fmtDate, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmpAvatar } from "@/components/EmpAvatar";
import { UploadButton } from "@/components/Upload";
import { SectionCard, StatusBadge } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Mail, Phone, MessageCircle, Save, Trash2, ArrowRightLeft, Plus } from "lucide-react";
import { toast } from "sonner";
import { DateField } from "@/components/DateField";

const waLink = (num) => {
  const raw = (num || "").trim();
  if (!raw) return null;
  let d = raw.replace(/\D/g, "");
  if (!d) return null;
  if (raw.startsWith("+")) return `https://wa.me/${d}`;        // explicit country code — trust it (e.g. +60 Malaysia)
  d = d.replace(/^0+/, "");
  if (d.startsWith("65") && d.length >= 10) return `https://wa.me/${d}`;  // already a SG intl number
  if (d.length === 8) return `https://wa.me/65${d}`;           // bare 8-digit SG local → add 65
  return `https://wa.me/${d}`;                                  // already has a country code — don't force 65
};
const bestPhone = (e) => e.contact_number || (e.personal_numbers || [])[0] || e.company_number || "";

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [emp, setEmp] = useState(null);
  const [balances, setBalances] = useState({});
  const [companies, setCompanies] = useState([]);
  const [edit, setEdit] = useState({});

  const load = useCallback(() => {
    api.get(`/employees/${id}`).then((r) => {
      const data = r.data;
      data.payroll_assignments = (data.payroll_assignments || []).map((a) => ({
        ...a,
        deduction_items: (a.deduction_items && a.deduction_items.length)
          ? a.deduction_items
          : (a.deductions ? [{ label: "Deductions", amount: a.deductions }] : []),
      }));
      setEmp(r.data); setEdit(data);
    }).catch(() => {});
    api.get(`/leaves/balances?employee_id=${id}`).then((r) => setBalances(r.data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/payroll-companies").then((r) => setCompanies(r.data)).catch(() => {}); }, [isAdmin]);

  const saveField = async (fields) => {
    try {
      await api.put(`/employees/${id}`, fields);
      toast.success("Saved");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveComp = () => saveField({
    payroll_assignments: (edit.payroll_assignments || []).map((a) => {
      const items = (a.deduction_items || [])
        .map((d) => ({ label: (d.label || "Deduction").trim() || "Deduction", amount: parseFloat(d.amount || 0) }))
        .filter((d) => d.amount);
      return {
        ...a,
        base_salary: parseFloat(a.base_salary || 0),
        commissions: parseFloat(a.commissions || 0),
        reimbursement: parseFloat(a.reimbursement || 0),
        deduction_items: items,
        deductions: parseFloat(items.reduce((s, d) => s + d.amount, 0).toFixed(2)),
      };
    }),
  });

  const updAssign = (i, patch) => setEdit({ ...edit, payroll_assignments: (edit.payroll_assignments || []).map((a, idx) => (idx === i ? { ...a, ...patch } : a)) });
  const addDed = (i) => updAssign(i, { deduction_items: [...((edit.payroll_assignments[i].deduction_items) || []), { label: "", amount: "" }] });
  const updDed = (i, j, patch) => updAssign(i, { deduction_items: (edit.payroll_assignments[i].deduction_items || []).map((d, k) => (k === j ? { ...d, ...patch } : d)) });
  const rmDed = (i, j) => updAssign(i, { deduction_items: (edit.payroll_assignments[i].deduction_items || []).filter((_, k) => k !== j) });
  const coName = (cid) => companies.find((c) => c.id === cid)?.name || "Default company";

  const [advances, setAdvances] = useState([]);
  const [advForm, setAdvForm] = useState({ amount: "", months: "3", payroll_company_id: "", reason: "" });
  const loadAdvances = useCallback(() => {
    if (isAdmin) api.get(`/employees/${id}/advances`).then((r) => setAdvances(r.data)).catch(() => {});
  }, [id, isAdmin]);
  useEffect(() => { loadAdvances(); }, [loadAdvances]);
  const grantAdvance = async () => {
    if (!(parseFloat(advForm.amount) > 0)) { toast.error("Enter an advance amount"); return; }
    try {
      await api.post(`/employees/${id}/advances`, {
        amount: parseFloat(advForm.amount),
        months: parseInt(advForm.months || "1", 10),
        payroll_company_id: advForm.payroll_company_id || null,
        reason: advForm.reason,
      });
      toast.success("Salary advance recorded");
      setAdvForm({ amount: "", months: "3", payroll_company_id: "", reason: "" });
      loadAdvances();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delAdvance = async (aid) => {
    if (!window.confirm("Delete this salary advance? Any scheduled recovery deductions will stop.")) return;
    try { await api.delete(`/advances/${aid}`); loadAdvances(); } catch (e) { toast.error(apiErr(e)); }
  };

  const [settleDate, setSettleDate] = useState("");
  const [settlement, setSettlement] = useState(null);
  const runSettlement = async () => {
    if (!settleDate) { toast.error("Pick a termination / resignation date first"); return; }
    try { const r = await api.get(`/employees/${id}/settlement?date=${settleDate}`); setSettlement(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const [xfer, setXfer] = useState({ to_payroll_company_id: "", effective_date: "", new_base_salary: "" });
  const runTransfer = async () => {
    if (!xfer.to_payroll_company_id) { toast.error("Choose the destination payroll company"); return; }
    if (!xfer.effective_date) { toast.error("Pick the effective transfer date"); return; }
    try {
      await api.post(`/employees/${id}/transfer`, {
        to_payroll_company_id: xfer.to_payroll_company_id,
        effective_date: xfer.effective_date,
        new_base_salary: parseFloat(xfer.new_base_salary || 0),
      });
      toast.success("Employee transferred");
      setXfer({ to_payroll_company_id: "", effective_date: "", new_base_salary: "" });
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const saveEnt = async () => {
    try {
      await api.put(`/employees/${id}/entitlements`, { entitlements: edit.entitlements });
      await api.put(`/employees/${id}/oil`, { oil_balance_hours: parseFloat(edit.oil_balance_hours || 0) });
      toast.success("Entitlements & OIL updated");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const remove = async () => {
    if (!window.confirm("Remove this employee and their login? This cannot be undone.")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Employee removed");
    navigate("/employees");
  };

  if (!emp) return null;
  const canEdit = isAdmin;
  const addrPairs = (emp.addresses && emp.addresses.length)
    ? emp.addresses.map((a) => [a.label ? `Address (${a.label})` : "Address", a.address])
    : (emp.address ? [["Address", emp.address]] : []);
  const infoPairs = [
    ["Company Email", emp.company_email], ["Personal Email", emp.personal_email], ["Contact", emp.contact_number],
    ...addrPairs, ["NRIC / FIN", emp.nric_fin], ["Gender", emp.gender], ["Passport No.", emp.passport_number], ["Work Permit No.", emp.work_permit_number], ["Date of Birth", fmtDate(emp.date_of_birth)],
    ["Start Date", fmtDate(emp.date_of_hire)], ["Probation Ends", emp.probation_end_date ? fmtDate(emp.probation_end_date) : null], ["Pass Holder", emp.pass_holder_type],
    ["Driving Licence", emp.driving_license_class && emp.driving_license_class !== "None" ? emp.driving_license_class : null],
    ["Licence Issued", emp.driving_license_issue_date ? fmtDate(emp.driving_license_issue_date) : null],
  ].filter(([, v]) => v);

  return (
    <div className="animate-fade-up">
      <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 -ml-2" data-testid="back-btn"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>

      <Card className="p-6 rounded-md border-border mb-6">
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <EmpAvatar emp={emp} size="h-20 w-20" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading font-extrabold text-3xl">{emp.full_name}</h1>
              <StatusBadge status={emp.status} />
            </div>
            <p className="text-muted-foreground mt-1">{emp.role} · {emp.department} · {emp.division}</p>
            <div className="flex items-center gap-2 mt-4">
              {waLink(bestPhone(emp)) ? (
                <a href={waLink(bestPhone(emp))} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="text-success"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button></a>
              ) : (
                <Button size="sm" variant="outline" className="text-muted-foreground opacity-50 cursor-not-allowed" disabled title="No contact number"><MessageCircle className="h-4 w-4 mr-1" /> WhatsApp</Button>
              )}
              <a href={`mailto:${emp.company_email || emp.email}`}><Button size="sm" variant="outline"><Mail className="h-4 w-4 mr-1" /> Email</Button></a>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(emp.contact_number || ""); toast.success("Copied"); }}><Phone className="h-4 w-4 mr-1" /> {emp.contact_number || "—"}</Button>
            </div>
          </div>
          {canEdit && <Button variant="outline" className="text-destructive" onClick={remove} data-testid="delete-employee"><Trash2 className="h-4 w-4" /></Button>}
        </div>
      </Card>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">Information</TabsTrigger>
          {canEdit && <TabsTrigger value="comp" data-testid="tab-comp">Compensation</TabsTrigger>}
          <TabsTrigger value="leave" data-testid="tab-leave">Leave Balances</TabsTrigger>
          {canEdit && <TabsTrigger value="ent" data-testid="tab-ent">Entitlements</TabsTrigger>}
          {canEdit && <TabsTrigger value="settle" data-testid="tab-settle">Settlement</TabsTrigger>}
          {canEdit && <TabsTrigger value="transfer" data-testid="tab-transfer">Transfer</TabsTrigger>}
        </TabsList>

        <TabsContent value="info" className="mt-4">
          <div className="grid md:grid-cols-2 gap-6">
            <SectionCard title="Personal">
              <dl className="space-y-3 text-sm">
                {infoPairs.map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-border pb-2"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right">{v || "—"}</dd></div>
                ))}
              </dl>
            </SectionCard>
            <SectionCard title="Employment & Banking">
              <dl className="space-y-3 text-sm">
                {[["Reporting Manager", emp.manager?.full_name], ["Bank Name", emp.bank_name], ["Bank Account", emp.bank_account_number], ["Clock Hours (month)", `${emp.clock_hours_month || 0}h`], ["OIL Balance", `${emp.oil_balance_hours || 0}h`]].map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 border-b border-border pb-2"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right">{v || "—"}</dd></div>
                ))}
              </dl>
              {emp.direct_reports?.length > 0 && (
                <div className="mt-4"><p className="overline mb-2">Direct Reports ({emp.direct_reports.length})</p>
                  <div className="space-y-1">{emp.direct_reports.map((r) => (
                    <div key={r.id} className="flex items-center gap-2 text-sm p-2 rounded-md hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/employees/${r.id}`)}><EmpAvatar emp={r} size="h-7 w-7" /> {r.full_name} <span className="text-muted-foreground">· {r.role}</span></div>
                  ))}</div>
                </div>
              )}
            </SectionCard>
          </div>
          {emp.emergency_contacts?.length > 0 && (
            <div className="mt-6">
              <SectionCard title="Emergency Contacts">
                <div className="grid sm:grid-cols-2 gap-4">
                  {emp.emergency_contacts.map((c, i) => (
                    <div key={i} className="rounded-md border border-border p-3 text-sm space-y-1" data-testid={`profile-emergency-${i}`}>
                      <p className="font-semibold">{c.name || "—"} <span className="text-muted-foreground font-normal">· {c.relation || "—"}</span></p>
                      {c.number && <p className="text-muted-foreground">{c.number}</p>}
                      {c.id_number && <p className="text-muted-foreground">ID: {c.id_number}</p>}
                      {c.marriage_or_dob && <p className="text-muted-foreground">Marriage / DOB: {fmtDate(c.marriage_or_dob)}</p>}
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}
        </TabsContent>

        {canEdit && (
          <TabsContent value="comp" className="mt-4">
            <SectionCard title="Compensation by Payroll Company (editable monthly)">
              <p className="text-xs text-muted-foreground mb-4">Each payroll company issues its own payslip. Enter base salary, commissions, reimbursement and deductions per company.</p>
              <div className="space-y-4">
                {(edit.payroll_assignments || []).map((a, i) => (
                  <div key={i} className="rounded-md border border-border p-4" data-testid={`comp-assign-${i}`}>
                    <p className="font-heading font-bold text-sm mb-3">{coName(a.payroll_company_id)}{i === 0 ? " · Primary (claims applied here)" : ""}</p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label>Base Salary ($)</Label><Input type="number" value={a.base_salary ?? ""} onChange={(e) => updAssign(i, { base_salary: e.target.value })} className="mt-1.5" data-testid={`comp-base-${i}`} /></div>
                      <div>
                        <Label>Commissions ($)</Label>
                        <div className="flex gap-2 mt-1.5"><Input type="number" value={a.commissions ?? ""} onChange={(e) => updAssign(i, { commissions: e.target.value })} data-testid={`comp-comm-${i}`} /><UploadButton label="Voucher" done={!!a.commission_file_id} onUploaded={(f) => updAssign(i, { commission_file_id: f.id })} /></div>
                      </div>
                      <div>
                        <Label>Reimbursement ($)</Label>
                        <div className="flex gap-2 mt-1.5"><Input type="number" value={a.reimbursement ?? ""} onChange={(e) => updAssign(i, { reimbursement: e.target.value })} data-testid={`comp-reimb-${i}`} /><UploadButton label="Voucher" done={!!a.reimbursement_file_id} onUploaded={(f) => updAssign(i, { reimbursement_file_id: f.id })} /></div>
                      </div>
                    </div>
                    <div className="mt-4 border-t border-border pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <Label>Deductions (itemized)</Label>
                        <Button type="button" size="sm" variant="ghost" onClick={() => addDed(i)} data-testid={`add-ded-${i}`}><Plus className="h-4 w-4 mr-1" /> Add deduction</Button>
                      </div>
                      {(a.deduction_items || []).length === 0 && <p className="text-xs text-muted-foreground">No deductions. e.g. Traffic Fine, Loan Recovery, Other.</p>}
                      <div className="space-y-2">
                        {(a.deduction_items || []).map((d, j) => (
                          <div key={j} className="flex gap-2 items-center" data-testid={`ded-row-${i}-${j}`}>
                            <Input placeholder="Label (e.g. Traffic Fine)" value={d.label ?? ""} onChange={(e) => updDed(i, j, { label: e.target.value })} className="flex-1" data-testid={`ded-label-${i}-${j}`} />
                            <Input type="number" placeholder="Amount" value={d.amount ?? ""} onChange={(e) => updDed(i, j, { amount: e.target.value })} className="w-32" data-testid={`ded-amount-${i}-${j}`} />
                            <Button type="button" size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => rmDed(i, j)} data-testid={`ded-rm-${i}-${j}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={saveComp} className="mt-4" data-testid="save-comp"><Save className="h-4 w-4 mr-1.5" /> Save Compensation</Button>
            </SectionCard>

            <SectionCard title="Salary Advances" className="mt-6">
              <p className="text-xs text-muted-foreground mb-4">Pay a lump sum this month and auto-recover it as a payslip deduction over the next N months.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
                <div><Label>Advance amount ($)</Label><Input type="number" value={advForm.amount} onChange={(e) => setAdvForm({ ...advForm, amount: e.target.value })} className="mt-1.5" data-testid="adv-amount" /></div>
                <div><Label>Recover over (months)</Label><Input type="number" min="1" value={advForm.months} onChange={(e) => setAdvForm({ ...advForm, months: e.target.value })} className="mt-1.5" data-testid="adv-months" /></div>
                <div>
                  <Label>Payroll company</Label>
                  <Select value={advForm.payroll_company_id || "__primary__"} onValueChange={(v) => setAdvForm({ ...advForm, payroll_company_id: v === "__primary__" ? "" : v })}>
                    <SelectTrigger className="mt-1.5" data-testid="adv-company"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover">
                      <SelectItem value="__primary__">Primary company</SelectItem>
                      {(edit.payroll_assignments || []).map((a) => <SelectItem key={a.payroll_company_id || "def"} value={a.payroll_company_id || "def"}>{coName(a.payroll_company_id)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Reason (optional)</Label><Input value={advForm.reason} onChange={(e) => setAdvForm({ ...advForm, reason: e.target.value })} className="mt-1.5" placeholder="e.g. Medical advance" data-testid="adv-reason" /></div>
              </div>
              <Button onClick={grantAdvance} className="mt-4" data-testid="grant-advance"><Plus className="h-4 w-4 mr-1.5" /> Record Advance</Button>

              <div className="mt-5 space-y-3">
                {advances.length === 0 && <p className="text-sm text-muted-foreground">No salary advances recorded.</p>}
                {advances.map((adv) => (
                  <div key={adv.id} className="rounded-md border border-border p-3" data-testid={`adv-row-${adv.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-heading font-bold text-sm">{fmtMoney(adv.amount)} <span className="text-muted-foreground font-normal">· recovered over {adv.months} month(s)</span></p>
                        <p className="text-xs text-muted-foreground mt-0.5">Paid out {adv.disbursement_period} · {fmtMoney(adv.monthly_amount)}/mo · ends {adv.last_recovery_period}{adv.reason ? ` · ${adv.reason}` : ""}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">Recovery: {adv.schedule.map((s) => s.period).join(", ")}</p>
                      </div>
                      <Button type="button" size="icon" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={() => delAdvance(adv.id)} data-testid={`adv-del-${adv.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </TabsContent>
        )}

        <TabsContent value="leave" className="mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Object.entries(balances).map(([t, b]) => (
              <Card key={t} className="p-4 rounded-md border-border">
                <p className="text-xs text-muted-foreground truncate">{t}</p>
                <p className="font-heading font-extrabold text-2xl font-mono-num mt-1">{b.remaining}<span className="text-sm text-muted-foreground font-normal">{t === "Off-in-lieu (OIL)" ? "h" : "d"}</span></p>
                <p className="text-xs text-muted-foreground mt-1">Used {b.used} / {b.entitled}</p>
              </Card>
            ))}
          </div>
        </TabsContent>

        {canEdit && (
          <TabsContent value="ent" className="mt-4">
            <SectionCard title="Annual Leave Entitlements (days)">
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(edit.entitlements || {}).map(([t, v]) => (
                  <div key={t} className="flex items-center justify-between gap-3"><Label className="flex-1">{t}</Label><Input type="number" step="0.5" value={v} onChange={(e) => setEdit({ ...edit, entitlements: { ...edit.entitlements, [t]: parseFloat(e.target.value || 0) } })} className="w-24" data-testid={`ent-${t}`} /></div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <Label>Off-in-Lieu (OIL) Balance (hours)</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <Input type="number" step="0.5" value={edit.oil_balance_hours ?? 0} onChange={(e) => setEdit({ ...edit, oil_balance_hours: e.target.value })} className="w-32" data-testid="ent-oil" />
                  <span className="text-xs text-muted-foreground">≈ {((parseFloat(edit.oil_balance_hours) || 0) / 8).toFixed(2)} off days (8h = 1 day)</span>
                </div>
              </div>
              <Button onClick={saveEnt} className="mt-4" data-testid="save-ent"><Save className="h-4 w-4 mr-1.5" /> Save Entitlements &amp; OIL</Button>
            </SectionCard>
          </TabsContent>
        )}

        {canEdit && (
          <TabsContent value="settle" className="mt-4">
            <SectionCard title="Final Settlement (Termination / Resignation)">
              <div className="flex items-end gap-3 flex-wrap">
                <div>
                  <Label>Last working day</Label>
                  <Input type="date" value={settleDate} onChange={(e) => setSettleDate(e.target.value)} className="mt-1.5 w-48" data-testid="settle-date" />
                </div>
                <Button onClick={runSettlement} disabled={!emp.date_of_hire} data-testid="settle-calc">Calculate</Button>
              </div>
              {!emp.date_of_hire && <p className="text-sm text-destructive mt-3">Set a Start Date on the Employment tab to enable leave accrual.</p>}

              {settlement && (
                <div className="mt-6 space-y-6" data-testid="settlement-result">
                  <p className="text-sm text-muted-foreground">Start {fmtDate(settlement.start_date)} → Last day {fmtDate(settlement.settlement_date)} · Accrual factor {settlement.accrual.factor} ({settlement.accrual.completed_months} full months + {settlement.accrual.partial_days}/{settlement.accrual.days_in_final_month})</p>

                  <div>
                    <p className="overline mb-2">Salary (prorated {settlement.salary_lines.length ? `× ${(settlement.salary_factor * 100).toFixed(0)}% of month` : ""})</p>
                    {settlement.salary_total_working_days != null && (
                      <p className="text-xs text-muted-foreground mb-2" data-testid="settle-working-days">Working days: {settlement.salary_days_worked}/{settlement.salary_total_working_days} ({settlement.working_days}-day week)</p>
                    )}
                    {settlement.salary_lines.map((s, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-border/50" data-testid={`settle-salary-${i}`}><span>{s.company_name} <span className="text-muted-foreground">(base {fmtMoney(s.base_salary)})</span></span><span className="font-mono-num">{fmtMoney(s.prorated_salary)}</span></div>
                    ))}
                    <div className="flex justify-between text-sm py-1 font-semibold"><span>Prorated salary total</span><span className="font-mono-num">{fmtMoney(settlement.salary_total)}</span></div>
                  </div>

                  <div>
                    <p className="overline mb-2">Leave — current vs total (days)</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="text-muted-foreground text-left"><th className="py-1">Type</th><th className="text-right">Total</th><th className="text-right">Current</th><th className="text-right">Used</th><th className="text-right">Bal (total)</th><th className="text-right">Bal (current)</th></tr></thead>
                        <tbody>
                          {settlement.leave.map((r) => (
                            <tr key={r.leave_type} className="border-t border-border/50" data-testid={`settle-leave-${r.leave_type}`}>
                              <td className="py-1">{r.leave_type}</td>
                              <td className="text-right font-mono-num">{r.total_entitled}</td>
                              <td className="text-right font-mono-num">{r.current_accrued}</td>
                              <td className="text-right font-mono-num">{r.used}</td>
                              <td className="text-right font-mono-num">{r.balance_vs_total}</td>
                              <td className={`text-right font-mono-num ${r.balance_vs_current < 0 ? "text-destructive" : ""}`}>{r.balance_vs_current}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Current = accrued to last day. Balance may be negative if leave taken exceeds accrual (allowed after probation, capped at the yearly total).</p>
                  </div>

                  <div className="flex justify-between text-sm"><span className="overline">OIL balance</span><span className="font-mono-num">{settlement.oil.entitled || 0}h ≈ {settlement.oil.off_days || 0} days <span className="text-muted-foreground">(utilise only, not encashable)</span></span></div>

                  <div>
                    <p className="overline mb-2">Outstanding claims (reimburse)</p>
                    {settlement.claims.length === 0 && <p className="text-sm text-muted-foreground">None.</p>}
                    {settlement.claims.map((c, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-border/50" data-testid={`settle-claim-${i}`}><span>{c.category} <span className="text-muted-foreground">{fmtDate(c.claim_date)}</span></span><span className="font-mono-num text-green-600">+{fmtMoney(c.amount)}</span></div>
                    ))}
                    {settlement.claims.length > 0 && <div className="flex justify-between text-sm py-1 font-semibold"><span>Claims total</span><span className="font-mono-num text-green-600">+{fmtMoney(settlement.claims_total)}</span></div>}
                  </div>

                  <div>
                    <p className="overline mb-2">Outstanding T-loans (company items — deduct value if unreturned)</p>
                    {settlement.tloans.length === 0 && <p className="text-sm text-muted-foreground">None.</p>}
                    {settlement.tloans.map((t, i) => (
                      <div key={i} className="flex justify-between text-sm py-1 border-b border-border/50" data-testid={`settle-tloan-${i}`}><span>{t.item_name} ×{t.quantity} <span className="text-muted-foreground">{t.due_date ? `due ${fmtDate(t.due_date)}` : ""}</span></span><span className="font-mono-num text-destructive">{t.value ? `-${fmtMoney(t.value)}` : "—"}</span></div>
                    ))}
                    {settlement.tloans.length > 0 && <div className="flex justify-between text-sm py-1 font-semibold"><span>T-loans total</span><span className="font-mono-num text-destructive">-{fmtMoney(settlement.tloans_total)}</span></div>}
                  </div>

                  <div className="flex justify-between items-center border-t-2 border-primary pt-3">
                    <span className="font-heading font-bold">Net settlement (salary + claims − T-loans)</span>
                    <span className="font-heading font-extrabold text-xl font-mono-num" data-testid="settle-net">{fmtMoney(settlement.net_settlement)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">Indicative figure. CPF and statutory deductions are handled via the normal payslip; leave/OIL balances are shown for HR to clear or encash at their discretion.</p>
                </div>
              )}
            </SectionCard>
          </TabsContent>
        )}

        {canEdit && (
          <TabsContent value="transfer" className="mt-4">
            <SectionCard title="Transfer to Another Payroll Company">
              <p className="text-xs text-muted-foreground mb-4">
                Moves this employee to a different payroll company (e.g. Shaabaz Pte Ltd → Shaabaz Kitchen Pte Ltd).
                This is a <b>clean cutover</b>: tenure <b>resets</b> to the effective date, leave balances and OIL <b>reset</b>,
                and all future payslips come from the new company. Past payslips stay as history.
              </p>
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label>Transfer To</Label>
                  <select
                    value={xfer.to_payroll_company_id}
                    onChange={(e) => setXfer({ ...xfer, to_payroll_company_id: e.target.value })}
                    className="mt-1.5 w-full h-10 rounded-md border border-border bg-background px-3 text-sm"
                    data-testid="transfer-company"
                  >
                    <option value="">Select company…</option>
                    {companies
                      .filter((c) => !(emp.payroll_assignments || []).some((a) => a.payroll_company_id === c.id))
                      .map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <Label>Effective Date</Label>
                  <DateField value={xfer.effective_date} onChange={(v) => setXfer({ ...xfer, effective_date: v })} className="mt-1.5" testid="transfer-date" />
                </div>
                <div>
                  <Label>New Base Salary ($)</Label>
                  <Input type="number" value={xfer.new_base_salary} onChange={(e) => setXfer({ ...xfer, new_base_salary: e.target.value })} className="mt-1.5" data-testid="transfer-salary" />
                </div>
              </div>
              <Button onClick={runTransfer} className="mt-4" data-testid="transfer-submit"><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Transfer Employee</Button>

              {(emp.transfer_history || []).length > 0 && (
                <div className="mt-8" data-testid="transfer-history">
                  <p className="overline mb-3">Transfer history</p>
                  <div className="space-y-2">
                    {[...emp.transfer_history].reverse().map((h, i) => (
                      <div key={i} className="rounded-md border border-border p-3 text-sm flex items-center justify-between">
                        <div>
                          <p className="font-medium">{h.from_company_names} <ArrowRightLeft className="inline h-3.5 w-3.5 mx-1 text-muted-foreground" /> {h.to_company_name}</p>
                          <p className="text-xs text-muted-foreground">Effective {fmtDate(h.effective_date)} · New base {fmtMoney(h.new_base_salary)} · by {h.by || "—"}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </SectionCard>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
